import React, { useEffect, useRef, useState } from 'react';
import { CAPAS, ponerCapa, leerCoordenadas } from './capasMapa';

const CENTRO = [3.0106, -76.4850];   // Santander de Quilichao

/**
 * Señalar una ubicación sobre el mapa, sin tener que estar parado en el sitio.
 *
 * Antes solo se podía tomar la ubicación con el GPS del celular, lo que obligaba a ir
 * hasta cada torre y cada caja. Aquí se hace clic sobre el mapa y listo. Queda anotado
 * que la ubicación se señaló a mano y no se midió, porque una cosa es un punto exacto y
 * otra uno aproximado: quien salga a buscar esa caja necesita saber la diferencia.
 */
export default function ElegirUbicacion({ inicial, nombre, alElegir, alCerrar }) {
  const contenedor = useRef(null);
  const mapa = useRef(null);
  const marca = useRef(null);
  const [punto, setPunto] = useState(
    inicial?.latitud ? { lat: Number(inicial.latitud), lon: Number(inicial.longitud) } : null);
  const [buscando, setBuscando] = useState(false);
  const [msg, setMsg] = useState('');
  // Satélite de entrada: con mapa de calles no se reconoce nada en la zona rural
  const [capa, setCapa] = useState('satelite');
  const [pegado, setPegado] = useState('');
  const capas = useRef({});
  const ponerRef = useRef(null);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const L = (await import('leaflet')).default;
      await import('leaflet/dist/leaflet.css');
      if (cancelado || !contenedor.current) return;

      const centro = punto ? [punto.lat, punto.lon] : CENTRO;
      const m = L.map(contenedor.current).setView(centro, punto ? 17 : 13);
      ponerCapa(L, m, capa, capas.current);
      mapa.current = m;
      // Se guarda para poder cambiar de capa sin volver a montar el mapa
      capas.current.L = L;

      const icono = L.divIcon({
        className: '',
        html: `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;background:#EF4444;
                 border:2px solid #fff;transform:rotate(-45deg);box-shadow:0 2px 8px rgba(0,0,0,.5)"></div>`,
        iconSize: [30, 30], iconAnchor: [15, 30],
      });

      const poner = (lat, lon) => {
        if (marca.current) m.removeLayer(marca.current);
        marca.current = L.marker([lat, lon], { icon: icono, draggable: true }).addTo(m);
        // Se puede arrastrar para afinar sin tener que volver a hacer clic
        marca.current.on('dragend', (ev) => {
          const p = ev.target.getLatLng();
          setPunto({ lat: p.lat, lon: p.lng });
        });
        setPunto({ lat, lon });
      };

      ponerRef.current = poner;
      if (punto) poner(punto.lat, punto.lon);
      m.on('click', (e) => poner(e.latlng.lat, e.latlng.lng));
      setTimeout(() => m.invalidateSize(), 120);
    })();
    return () => { cancelado = true; if (mapa.current) { mapa.current.remove(); mapa.current = null; } };
    // eslint-disable-next-line
  }, []);

  // Ir a la ubicación del equipo, útil cuando sí se está en el sitio
  const usarGps = () => {
    if (!navigator.geolocation) { setMsg('Este equipo no puede tomar la ubicación'); return; }
    setBuscando(true); setMsg('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBuscando(false);
        const { latitude, longitude } = pos.coords;
        if (mapa.current) mapa.current.setView([latitude, longitude], 18);
        setPunto({ lat: latitude, lon: longitude, gps: true });
        setMsg(`Ubicación tomada con ${Math.round(pos.coords.accuracy)} m de precisión`);
      },
      (err) => {
        setBuscando(false);
        setMsg({ 1: 'No diste permiso para la ubicación', 2: 'No se pudo determinar la ubicación',
                 3: 'La ubicación tardó demasiado' }[err.code] || 'No se pudo tomar la ubicación');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };

  const cambiarCapa = (cual) => {
    setCapa(cual);
    const L = capas.current.L;
    if (L && mapa.current) ponerCapa(L, mapa.current, cual, capas.current);
  };

  /**
   * Llevar el mapa a lo que se haya pegado.
   *
   * Se avisa si el punto cae lejos del Cauca: copiando enlaces es fácil traer el sitio
   * equivocado, y una torre puesta en otro departamento no se nota hasta que alguien sale
   * a buscarla. Se avisa pero no se impide: el que sabe, sabe.
   */
  const irAlPegado = () => {
    const p = leerCoordenadas(pegado);
    if (!p) {
      setMsg('No reconozco esas coordenadas. Pega el enlace de Google Maps tal cual, ' +
             'o dos números como 3.0106, -76.4850');
      return;
    }
    if (mapa.current) mapa.current.setView([p.lat, p.lon], 18);
    if (ponerRef.current) ponerRef.current(p.lat, p.lon);
    setPegado('');
    setMsg(p.lejos
      ? '⚠ Ese punto queda lejos de la zona donde trabajas. Revísalo antes de guardar.'
      : 'Listo. Arrastra el marcador si hay que afinarlo.');
  };

  const confirmar = () => {
    if (!punto) { setMsg('Haz clic en el mapa para señalar el lugar'); return; }
    alElegir({
      latitud: punto.lat.toFixed(7),
      longitud: punto.lon.toFixed(7),
      // Se distingue lo medido de lo señalado: cambia cuánto se puede confiar en el punto
      ubicacion_origen: punto.gps ? 'gps' : 'mapa',
    });
    alCerrar();
  };

  return (
    <div style={s.fondo} onClick={alCerrar}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.cabecera}>
          <div>
            <h3 style={s.h3}>📍 ¿Dónde queda {nombre || 'este punto'}?</h3>
            <div style={s.sub}>Haz clic en el mapa. Puedes arrastrar el marcador para afinar.</div>
          </div>
          <button onClick={alCerrar} style={s.cerrar}>✕</button>
        </div>

        {msg && <div style={s.msg} onClick={() => setMsg('')}>{msg}</div>}

        <div style={s.herramientas}>
          {/* Satélite o calles. En la zona rural el mapa de calles está casi vacío y no
              hay contra qué reconocer un cerro o un techo; con la foto sí. */}
          <div style={s.capas}>
            {Object.entries(CAPAS).map(([k, c]) => (
              <button key={k} onClick={() => cambiarCapa(k)}
                style={{ ...s.capaBtn, ...(capa === k ? s.capaBtnOn : {}) }}>
                {c.nombre}
              </button>
            ))}
          </div>

          {/* Traer el punto desde Google Maps: allá se busca con mejor imagen y hasta con
              vista de calle, se copia, y aquí se pega. Acepta el enlace tal cual o solo
              las coordenadas. */}
          <input value={pegado} onChange={e => setPegado(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && irAlPegado()}
            placeholder="…o pega aquí el enlace de Google Maps o las coordenadas"
            style={s.pegar} />
          <button onClick={irAlPegado} style={s.btn} disabled={!pegado.trim()}>Ir</button>
        </div>

        <div ref={contenedor} style={s.mapa} />

        <div style={s.pie}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {punto ? (
              <span style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.7)' }}>
                {punto.lat.toFixed(7)}, {punto.lon.toFixed(7)}
                <span style={{ color: punto.gps ? '#86EFAC' : '#FDE047', marginLeft: '8px', fontSize: '11.5px' }}>
                  {punto.gps ? '· tomada en el sitio' : '· señalada en el mapa'}
                </span>
              </span>
            ) : (
              <span style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.35)' }}>
                Todavía no has señalado el lugar
              </span>
            )}
          </div>
          <button onClick={usarGps} disabled={buscando} style={s.btn}>
            {buscando ? 'Buscando…' : '📡 Estoy aquí'}
          </button>
          <button onClick={alCerrar} style={s.btn}>Cancelar</button>
          <button onClick={confirmar} style={s.btnP} disabled={!punto}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  fondo:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 200 },
  modal:    { background: '#0F1629', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '18px', width: '100%', maxWidth: '820px' },
  cabecera: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  h3:       { fontSize: '15px', fontWeight: '700', color: '#fff', marginBottom: '2px' },
  sub:      { fontSize: '12px', color: 'rgba(255,255,255,0.45)' },
  cerrar:   { background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '16px' },
  herramientas: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '9px', flexWrap: 'wrap' },
  capas:      { display: 'flex', gap: '4px' },
  capaBtn:    { padding: '6px 11px', borderRadius: '7px', fontSize: '12px', cursor: 'pointer', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.55)' },
  capaBtnOn:  { background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.5)', color: '#A5B4FC', fontWeight: '600' },
  pegar:      { flex: '1 1 260px', minWidth: 0, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '7px', padding: '7px 11px', color: '#fff', fontSize: '12.5px' },
  mapa:     { height: '52vh', minHeight: '320px', borderRadius: '11px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', background: '#0B1020', cursor: 'crosshair' },
  pie:      { display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap' },
  btn:      { padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', cursor: 'pointer', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.65)' },
  btnP:     { padding: '8px 18px', borderRadius: '8px', fontSize: '12.5px', cursor: 'pointer', background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.5)', color: '#86EFAC', fontWeight: '600' },
  msg:      { background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', color: '#A5B4FC', padding: '8px 12px', borderRadius: '8px', marginBottom: '9px', fontSize: '12.5px', cursor: 'pointer' },
};
